import { createSelector } from 'reselect';
import moment from 'moment';

const DEFAULT_EMPTY_HASH = {};
const DEFAULT_EMPTY_ARRAY = [];
const companies = (state) => state.companies || DEFAULT_EMPTY_ARRAY;
const hcpAssistCompanies = (state) => state.hcpAssistCompanies || DEFAULT_EMPTY_ARRAY;
const hcpAssistActivityLogs = (state) => state.hcpAssistActivityLogs || DEFAULT_EMPTY_ARRAY;
const hcpAssistCallLogs = (state) => state.hcpAssistCallLogs || DEFAULT_EMPTY_ARRAY;
export const getCompanyDetail = (state) => state.companyDetail || DEFAULT_EMPTY_HASH;
export const getReputationSources = (state) => state.reputationSources || DEFAULT_EMPTY_HASH;
export const getEmployee = (state) => state.employee || DEFAULT_EMPTY_HASH;
export const getChannelPartners = (state) => state.channel_partners || DEFAULT_EMPTY_ARRAY;
const accolades = (state) => state.accolades || DEFAULT_EMPTY_ARRAY;
const verificationRecords = (state) => state.verification_records || DEFAULT_EMPTY_HASH;
const hcpAssistConfiguration = (state) => state.hcpAssistConfiguration || DEFAULT_EMPTY_HASH;
const balanceAccountRestrictions = (state) => state.balanceAccountRestrictions || DEFAULT_EMPTY_HASH;

export const getBalanceAccountRestrictions = createSelector(
  balanceAccountRestrictions,
  (restricted) => restricted || DEFAULT_EMPTY_HASH
);

export const getCompanyEmployees = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.employees || DEFAULT_EMPTY_ARRAY
);

export const getCompanies = createSelector(companies, (companies) => companies || DEFAULT_EMPTY_ARRAY);

export const getHcpAssistLogs = createSelector(
  hcpAssistActivityLogs,
  (hcpAssistActivityLogs) => hcpAssistActivityLogs || DEFAULT_EMPTY_ARRAY
);

export const getHcpAssistConfiguration = createSelector(
  hcpAssistConfiguration,
  (organizationConfiguration) => organizationConfiguration || DEFAULT_EMPTY_HASH
);

export const getHcpAssistRequestGuideIndustries = createSelector(
  hcpAssistConfiguration,
  (organizationConfiguration) =>
    organizationConfiguration?.request_guide_industries?.data?.map((i) => i.industry) || DEFAULT_EMPTY_ARRAY
);

export const getBasicInfo = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.basic_info || DEFAULT_EMPTY_HASH
);

export const getCompanyTypeOptions = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.type_options || DEFAULT_EMPTY_ARRAY
);

export const getOrganizationId = createSelector(getBasicInfo, (basicInfo) => basicInfo.organization_id);

export const getOrganizationUuid = createSelector(getBasicInfo, (basicInfo) => basicInfo.uuid || '');

export const getBillingInfo = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.billing_info || DEFAULT_EMPTY_HASH
);

export const getAvailableCreditsTotalAmount = createSelector(
  getBillingInfo,
  (billingInfo) => billingInfo.available_credit || 0
);

export const getStripeManagedAccount = createSelector(
  getBillingInfo,
  (billingInfo) => billingInfo.stripe_accounts || DEFAULT_EMPTY_HASH
);

export const getFeatures = createSelector(getBillingInfo, (billingInfo) => billingInfo.features || DEFAULT_EMPTY_ARRAY);

export const getCardReaderStatus = createSelector(getFeatures, (features) =>
  features.map((x) => x.name).includes('stripe_terminal')
);

export const getBillingPlanSummary = createSelector(
  getBillingInfo,
  (billingInfo) => billingInfo.plan_summary || DEFAULT_EMPTY_HASH
);

export const getRenewalDateFormatted = createSelector(getBillingPlanSummary, (planSummary) => {
  const renewalDate = planSummary.renewal_date || '';

  return moment(renewalDate).format('YYYY-MM-DD');
});

export const getBills = createSelector(getBillingInfo, (billingInfo) => billingInfo.bills || DEFAULT_EMPTY_ARRAY);

export const getFailedBills = createSelector(getBills, (bills) => bills.filter((bill) => bill.has_failed));

export const getBillDueAmount = createSelector(getBills, (bills) =>
  bills.reduce((acc, bill) => acc + bill.due_amount, 0)
);

export const getActivityFeed = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.activity_feed || DEFAULT_EMPTY_ARRAY
);

export const getAccolades = createSelector(accolades, (accolades) => accolades);

export const getVerificationRecords = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.verification_records || DEFAULT_EMPTY_HASH
);

export const getExperiments = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.experiments || DEFAULT_EMPTY_ARRAY
);

export const getPaymentDetails = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.payment_details || DEFAULT_EMPTY_ARRAY
);

export const getConsumerLendingConfiguration = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.consumer_lending || DEFAULT_EMPTY_HASH
);

export const isConsumerLendingEnabled = createSelector(
  getConsumerLendingConfiguration,
  (consumerLending) => consumerLending.enabled || false
);

export const getCurrentInstapayBalance = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.current_instapay_balance
);

export const getTransactionCount = createSelector(
  getPaymentDetails,
  (paymentDetails) => paymentDetails.transaction_count || 0
);

export const getCurrentStandardBalance = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.current_standard_balance
);

export const getLastTransactionDate = createSelector(getPaymentDetails, (paymentDetail) => {
  const lastTransactionDate = paymentDetail.last_transaction_date;

  return lastTransactionDate == null ? 'N/A' : moment(lastTransactionDate).format('YYYY-MM-DD');
});

export const getGrossMerchandiseVolumeFor7Days = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.gross_merchandise_volume_7_days || 'N/A'
);

export const getGrossMerchandiseVolumeFor30Days = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.gross_merchandise_volume_30_days || 'N/A'
);

export const getGrossMerchandiseVolumeForCredit30Days = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.gross_merchandise_volume_credit_30_days || 'N/A'
);

export const getGrossMerchandiseVolumeForOther30Days = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.gross_merchandise_volume_other_30_days || 'N/A'
);

export const getGrossInstant30Days = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.gross_instant_30_days
);

export const getInstapayDebitNegativeBalance = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.instapay_debit_negative_balance
);

export const hasPlaidTransactions = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.has_plaid_transactions || false
);

export const hasValidPlaidCredential = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.has_valid_plaid_credential || false
);

export const getOverallCreditPercentTotalCharges = createSelector(
  getPaymentDetails,
  (paymentDetail) => paymentDetail.overall_credit_percent_total_charges
);

export const getUsageBasedPricingExperiment = createSelector(getExperiments, (experiments) => {
  const usageBasedPricing = experiments.find((experiment) => experiment.name === 'usage_based_pricing');
  return usageBasedPricing || {};
});

export const isUsageBasedPricingEnabled = createSelector(
  getUsageBasedPricingExperiment,
  (usageBasedPricing) => usageBasedPricing.enabled
);

export const getEcosystemApp = (assetId) => (state) => state[assetId];

export const hasWatscoAttribution = createSelector(
  getCompanyDetail,
  (companyDetail) => companyDetail.basic_info.has_watsco_attribution || false
);
